import type { TourThemeConfig } from '../../../types/themed/tour.types';

import { setTheme } from '../../../actions/ui/set-theme';
import { DefaultTheme } from '../../../components/themed/defaultTheme';
import { generateTourTheme } from '../../../components/themed/generate-tour-theme';
import store from '../../../reducers/index';

import { deviceSize } from '../../../style/responsive.styled';

export function applyTheme() {
  const {
    colors: defaultColors,
    texts: defaultTexts,
    resultsScreen,
  } = DefaultTheme;

  const colors = {
    ...defaultColors,

    primary: '#101820',
    primaryInverted: '#FFFFFF',
    accent: '#101820',
    promo: '#878B8F',
    secondary: '#4C5258',
    decorators: '#CD2C5B',
    grey: '#EEEFEE',
  };

  const baseText = {
    fontFamily: 'Roboto Regular New',
    fontWeight: 'normal',
    fontStyle: 'normal',
  };

  const texts = {
    ...defaultTexts,

    default: baseText,
    header: {
      fontFamily: 'Roboto Bold New',
      fontWeight: '700',
      fontSize: '20px',
      lineHeight: '27px',
      letterSpacing: '1px',
    },

    buttons: {
      fontFamily: 'Roboto Bold New',
      fontWeight: '700',
      fontSize: '12px',
      lineHeight: '20px',
      letterSpacing: '0.2px',
    },

    descriptions: {
      regular: {
        fontFamily: 'Roboto Regular New',
        fontWeight: '400',
        fontSize: '16px',
        lineHeight: '21px',
      },
      bold: {
        fontFamily: 'Roboto Bold New',
        fontWeight: '700',
        fontSize: '16px',
        lineHeight: '21px',
      },
      light: {
        fontFamily: 'Roboto Regular New',
        fontWeight: '300',
        fontSize: '14px',
        lineHeight: '18px',
      },
    },
  };

  const tourConfigOptions = { hideHeroImage: false };
  const tourConfig = generateTourTheme(colors, texts, tourConfigOptions);

  const tourConfigModified: TourThemeConfig = {
    ...tourConfig,

    main_wrapper: {
      additional_mq: {
        [deviceSize.tablet]: `
          justify-content: flex-start;

          margin-top: 120px;
        `,
      },
    },

    title: {
      ...tourConfig.title,

      additional_css: `
        text-transform: capitalize;
      `,
    },

    subtitle: {
      ...tourConfig.subtitle,

      additional_css: `
        padding: 15px 10% 30px;

        letter-spacing: 0.3px;

         @media (min-width: ${deviceSize.tablet}px) {
          padding: 22px 10% 12px;
        }
        
        @media (min-width: ${deviceSize.laptop}px) {
          padding: 22px 10% 30px;
        }
      `,
    },

    cta_container: {
      ...tourConfig.cta_container,

      additional_css: `
        order: 1;

        gap: 10px;
        
        justify-content: space-evenly;

        margin: 35px 10px 10px 10px;

        @media (min-width: ${deviceSize.tablet}px) {
          margin-top: 15px;
        }
        
        @media (min-width: ${deviceSize.laptop}px) {
          margin-top: 35px;
        }

        @media (max-height: 597px) {
          margin-top: auto;
        }
      `,
    },

    cta_button: {
      ...tourConfig.cta_button,

      width: '165px',
      height: '48px',
      border: 'unset',
      fontSize: '12px',
      lineHeight: '20px',
      additional_css: `
        order: 2;

        padding: 7px;

        justify-content: center;

        background-color: ${colors.accent} !important;
        border: none !important;
        border-radius: 0px !important;

        letter-spacing: 0.2px;
        text-transform: uppercase;
        color: ${colors.primaryInverted} !important;

        &::before {
          margin-right: 8px;

          width: 24px;
          height: 24px;
        }
      `,
      additional_mq: {
        [deviceSize.tablet]: `
          width: 205px;
        `,
      },
    },

    gallery_text_box: {
      ...tourConfig.gallery_text_box,

      color: colors.primaryInverted,
      sticky_background: 'rgba(16, 24, 32, 0.8)',
      sticky_color: colors.primaryInverted,

      additional_css: `
        height: 65px;

        padding: 0;

        align-content: center;

        font-size: 14px;
        line-height: 18px !important;
        color: ${colors.secondary};
        
        @media (min-width: ${deviceSize.tablet}px) {
          height: 80px;

          font-size: 16px;
          line-height: 21px !important;
          color: ${colors.primaryInverted};
        }
      `,
    },
  };

  store.dispatch(setTheme('colors', colors));
  store.dispatch(setTheme('texts', texts));
  store.dispatch(setTheme('resultsScreen', resultsScreen));
  store.dispatch(setTheme('tour', tourConfigModified));
}
